import { Component, OnInit } from '@angular/core';
import {
  CandidateDataLoadState,
  CandidateListResultDetailsDto,
  DetailClient,
  ElectionAreaDetailsDto,
  ElectionClient,
  ElectionDto,
  ElectionType,
  ExportDataClient,
  FaElectionOverviewDto,
  FileResponse, OverviewClient, ResultType,
  VotingDetailsDto
} from '@shared/clients/api.generated';
import { SvgIcon } from '@valg-shared/utils/svg-icon';
import { VotingDetails } from '@shared/components/voting-details/voting-details.model';
import { ReferendumStatus } from '@shared/components/cards/referendum-status/referendum-status.model';
import { PartyOrCandidateListBase, PartyOrCandidateListDetail } from "@shared/components/expandable-party-list/expandable-party-table.model";
import { CountStatus } from "@shared/components/count-status/count-status.component";
import { TranslateService } from "@ngx-translate/core";
import { ActivatedRoute } from "@angular/router";
import { saveFile } from "@valg-shared/utils/file-utils";
import { Observable } from "rxjs";
import { SpinnerService } from "@valg-shared/services/spinner/spinner.service";
import { ExportFileService } from "@valg-shared/services/export-file/export-file.service";
import { ElectionOverviewService } from 'app/pages/election-overview/election-overview.service';
import { SidenavigationLink } from '@shared/components/sidenavigation/sidenavigation.model';
@Component({
  selector: 'valg-dk-polling-district',
  templateUrl: './polling-district.component.html',
  providers: [ SpinnerService ]
})
export class DetailsPollingDistrictComponent implements OnInit {

  readonly SvgIcon = SvgIcon;
  election?: ElectionDto;
  ElectionType = ElectionType;
  parties: PartyOrCandidateListBase[] = [];
  isRmElection = false;
  votingDetails?: VotingDetails;
  electionAreaId: string;
  filterLinks: SidenavigationLink[] = [];
  countStatusUpdated: Date | undefined;
  countStatus: CountStatus[] = [];
  referendumStatus: ReferendumStatus[] = [];
  candidateDataLoadState: CandidateDataLoadState = CandidateDataLoadState.NotLoaded;
  resultType: ResultType = ResultType.NoResults;
  isMigratedElection = false;


  constructor(
    route: ActivatedRoute,
    private readonly detailClient: DetailClient,
    private readonly electionClient: ElectionClient,
    private readonly translateService: TranslateService,
    private readonly exportDataClient: ExportDataClient,
    private readonly spinner: SpinnerService,
    private readonly exportFileService: ExportFileService,
    private readonly overviewClient: OverviewClient,
    private readonly electionOverviewService: ElectionOverviewService
    ) {
    this.electionAreaId = route.snapshot.params['electionAreaId'];
  }

  ngOnInit(): void {
    this.initialize()
  }

  initialize() {
    this.electionClient.getCurrentElection().subscribe((result) => {
      this.election = {
        "id": result.id,
        "electionName": result.electionName,
        "electionDate": result.electionDate,
        "electionType": result.electionType,
        "title": result.electionType === ElectionType.Referendum ? result.referendumTitle : undefined
      };
      this.getNavigationFilterLinks();
      this.checkIfMigratedElection();
      switch(result.electionType) {
        case ElectionType.GeneralElection:
          this.getGeElectionAreaDetail();
          break;
        case ElectionType.EuropeanParliamentElection:
          this.getEvElectionAreaDetail();
          break;
        case ElectionType.Referendum:
          this.getFaElectionAreaDetail();
          break;
        default:
          this.getRmElectionAreaDetail();
          this.isRmElection = true;
          break;
      }
    });
  }

  private getGeElectionAreaDetail() {
    this.detailClient.getGeElectionAreaDetails(this.electionAreaId).subscribe((result) => {
      this.dataMapping(result)
    })
  }

  private getRmElectionAreaDetail() {
    this.detailClient.getRmElectionAreaDetails(this.electionAreaId).subscribe((result) => {
      this.dataMapping(result)
    })
  }
  private dataMapping(result: ElectionAreaDetailsDto) {
    this.resultType = result.resultType;
    this.candidateDataLoadState = result.candidateDataLoadState;
    this.countStatusUpdated = result.countStatusDto?.lastUpdate;
    this.countStatus = [
      {
        name: "count_status.voting_percentage",
        percentage: result.countStatusDto?.percentage?.votingPercentage
      },
      {
        name: "count_status.voting_prec_count_percentage",
        percentage: result.countStatusDto?.percentage?.preCountPercentage
      },
      {
        name: "count_status.voting_fine_count_percentage",
        percentage: result.countStatusDto?.percentage?.fineCountPercentage
      }
    ];
    this.setPartiesOrCandidateLists(result.candidateListResultDetailsList);
    this.setVotingDetails(result.votingDetails);
  }

  private getEvElectionAreaDetail() {
    this.detailClient.getEvElectionAreaDetails(this.electionAreaId).subscribe((result) => {
     this.dataMapping(result)
    })
  }

  private getFaElectionAreaDetail() {
    this.detailClient.getFaElectionAreaDetails(this.electionAreaId).subscribe((result) => {
      this.resultType = result.resultType;
      this.candidateDataLoadState = result.candidateDataLoadState;
      this.countStatusUpdated = result.countStatusDto?.lastUpdate;
      this.resultType = result.resultType;
      this.countStatus = [
        {
          name: "count_status.voting_percentage",
          percentage: result.countStatusDto?.percentage?.votingPercentage
        },
        {
          name: "count_status.voting_prec_count_percentage",
          percentage: result.countStatusDto?.percentage?.preCountPercentage
        },
        {
          name: "count_status.voting_fine_count_percentage",
          percentage: result.countStatusDto?.percentage?.fineCountPercentage
        }
      ];
      this.setEvPartiesTable(result.faElectionOverviewDto);
      this.setVotingDetails(result.votingDetails);
    })
  }

  private setVotingDetails(votingDetailsDto?: VotingDetailsDto) {
    this.votingDetails = {
      numberOfVoters: votingDetailsDto?.numberOfEligibleVoters ?? 0,
      pollingStationName: votingDetailsDto?.electionAreaDetail?.pollingStationName,
      electionAreaName: votingDetailsDto?.electionAreaDetail?.electionAreaName,
      address: votingDetailsDto?.electionAreaDetail?.address,
      validVotes: {
        value: votingDetailsDto?.validVotesCount.validVotesTotal ?? 0,
        diff: votingDetailsDto?.validVotesCount.changeComparedToPreviousElection
      },
      invalidVotes: {
        value: votingDetailsDto?.invalidVotesCount.invalidVotesTotal ?? 0,
        diff: votingDetailsDto?.invalidVotesCount.changeComparedToPreviousElection
      },
      blankVotes: {
        value: votingDetailsDto?.blankVotesCount.blankVotesTotal ?? 0,
        diff: votingDetailsDto?.blankVotesCount.changeComparedToPreviousElection
      },
      totalVotes: {
        value: votingDetailsDto?.totalVotesCount.totalVotes ?? 0,
        diff: votingDetailsDto?.totalVotesCount.totalChangeComparedToPreviousElection
      }
    }
  }

  private setPartiesOrCandidateLists(result: CandidateListResultDetailsDto[]) {
    this.parties = result?.map((details) => {
      return {
        title: details.displayName == "" ? this.translateService.instant("simple_text.independent_candidate") : details.displayName ,
        votes: this.resultType == ResultType.NoResults ? "" : details.numberOfVotes,
        percentage: details.percentages,
        diff: details.votingDifferencePreviousElection,
        percentage_diff: details.differencePercentages,
        listOrganisation: details.listOrganisationType,
        hasSpecifiedPersonalVotes: details.hasSpecifiedPersonalVotes,
        id: crypto.randomUUID(),
        children: details.candidateResultListWithListVotes.map((cr) => {
          return {
            name: cr.displayName,
            votes: cr.numberOfVotes,
            id: crypto.randomUUID()
          } as PartyOrCandidateListDetail
        })
      } as PartyOrCandidateListBase
    });
  }

  private setEvPartiesTable(result?: FaElectionOverviewDto) {
    this.referendumStatus = result? [
      {
        option: this.translateService.instant("simple_text.yes"),
        votes: result?.yesVotes,
        percentage: result?.yesPercentage
      },
      {
        option: this.translateService.instant("simple_text.no"),
        votes: result?.noVotes,
        percentage: result?.noPercentage
      }
    ] as ReferendumStatus[] : [];
  }

  private getNavigationFilterLinks() {
    this.overviewClient.getElectionSideNavigation().subscribe((result) => {
      if (this.election?.electionType === ElectionType.MunicipalElection) {
        this.filterLinks = this.electionOverviewService.mapMunicipalSideNavigation(result);
      } else if (this.election?.electionType === ElectionType.RegionalElection) {
        this.filterLinks = this.electionOverviewService.mapRegionalSideNavigation(result);
      } else {
        this.filterLinks = this.electionOverviewService.mapConstituencies(result);
      }
    });
  }

  onDownloadReportClick() {
    const handler = this.getExportDataCsv();
    this.spinner.attachSpinner(handler).subscribe(result => {
      const fileName = this.exportFileService.getReportFileNameWithDate(result.fileName, ".csv");
      saveFile(result.data, fileName);
    });
  }

  private getExportDataCsv(): Observable<FileResponse> {
    const currentElection = this.election;
    switch (currentElection?.electionType) {
      case ElectionType.GeneralElection:
        return this.exportDataClient.exportFvDataCsv(currentElection?.id, null,
          null, this.electionAreaId);
      case ElectionType.EuropeanParliamentElection:
        return this.exportDataClient.exportEvDataCsv(currentElection?.id, null,
          null, this.electionAreaId);
      case ElectionType.Referendum:
        return this.exportDataClient.exportFaDataCsv(currentElection?.id, null,
          null, this.electionAreaId);
      case ElectionType.RegionalElection:
        return this.exportDataClient.exportRvDataCsv(currentElection?.id, null,
          null, this.electionAreaId);
      case ElectionType.MunicipalElection:
        return this.exportDataClient.exportKvDataCsv(currentElection?.id, null, this.electionAreaId);
      default:
        throw new Error('Election type is not found.');
    }
  }

  checkIfMigratedElection(): void {
    if (!this.election?.electionDate) {
      return;
    }

    const electionDate = new Date(this.election.electionDate);
    this.isMigratedElection = this.electionOverviewService.isMigratedElection(electionDate);
  }

}
